import { FC, useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash-es';
import { Box, SkeletTable } from '@alf/uikit';
import { PersonalOfficeWrapper } from '@namespace/account';
import { useConfig, useOtherSettings } from '@namespace/cms';
import { Filter, NoData } from '@namespace/common';
import { useDeviceStore } from '@namespace/device';
import { stringifySearchParams } from '@namespace/helpers';
import { useLocation } from '@namespace/i18n';
import { useHistory } from '@namespace/router';
import { useTransactionHistory } from '../../hooks/useTransactionHistory';
import {
  Filter as TransactionFilter,
  useTransactionsFilter
} from '../../hooks/useTransactionsFilter';
import { SourceItem } from '../../types/history';
import { formatData, FormattedItem } from '../../utils/history';
import TransactionsHistoryCardList from './TransactionsHistoryCardList';
import TransactionsHistoryTable from './TransactionsHistoryTable';

import styles from './index.module.css';

interface RenderTransactionHistoryProps {
  transactionHistory: { columns: string[] };
  historyData: FormattedItem[];
  dataFields: string[];
  setPage: () => void;
  isFetching: boolean;
  isShowMore: boolean;
}

const RenderTransactionHistory: FC<RenderTransactionHistoryProps> = (props) => {
  const { isMobile } = useDeviceStore();

  if (isMobile) {
    return <TransactionsHistoryCardList {...props} />;
  }
  return <TransactionsHistoryTable {...props} />;
};

const TransactionsHistory = ({ id: configId }: { id: string }) => {
  const { config } = useConfig(configId);
  const {
    dataFields = [],
    transactionHistory = {},
    type,
    amountFilterOptions
  } = config;
  const { filterFields } = transactionHistory;

  const { payments = {} } = useOtherSettings();
  const { receiptOnTransactionHistoryEnabled } = payments;

  const filteredDataFields = useMemo(
    () =>
      receiptOnTransactionHistoryEnabled
        ? dataFields
        : dataFields.filter((item: string) => item !== 'receipt'),
    [dataFields, receiptOnTransactionHistoryEnabled]
  );

  const {
    getFieldsConfig: getFilterFieldsConfig,
    filter,
    setFilter
  } = useTransactionsFilter({ type, filterFields, amountFilterOptions });

  const {
    data = [],
    isFetching,
    isFetchingError,
    page,
    totalPages,
    resetHistory,
    dataType,
    requestHistory
  } = useTransactionHistory(filter, type);

  const shouldRenderData = !isEmpty(data) && dataType === type;

  const isShowMore = page && page < (totalPages as number);

  const { pathname } = useLocation();
  const { push } = useHistory();

  const onFilterSet = useCallback(
    (newFilter: TransactionFilter) => {
      resetHistory();
      setFilter((currentFilter) => ({
        ...currentFilter,
        ...newFilter
      }));
      const newUrl = stringifySearchParams(newFilter);
      push(`${pathname}?${newUrl}`);
    },
    [pathname, push, resetHistory, setFilter]
  );

  const onPageChange = useCallback(() => {
    requestHistory();
  }, [requestHistory]);

  return (
    <Box
      component={PersonalOfficeWrapper}
      direction="column"
      className={styles.wrapper}
    >
      <Filter
        onSet={onFilterSet}
        filterConfig={getFilterFieldsConfig()}
        enableFilterBtn={isFetchingError}
      />
      {isEmpty(data) && !isFetching && (
        <NoData customKey="web.personalOffice.balance.history.noData" />
      )}
      {isFetching && !page && (
        <SkeletTable
          classNames={{
            classNameWrapper: styles.skeletWrapper,
            classNameItem: styles.skeletNameItem
          }}
        />
      )}
      {shouldRenderData && (
        <RenderTransactionHistory
          transactionHistory={transactionHistory}
          historyData={formatData(data as SourceItem[], type)}
          dataFields={filteredDataFields}
          setPage={onPageChange}
          isFetching={isFetching as boolean}
          isShowMore={isShowMore as boolean}
        />
      )}
    </Box>
  );
};

export default TransactionsHistory;
